import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { NextIcon } from '@platform/ui-kit';
import type { ModuleSection } from '../api';

/**
 * A landing page — where entering a module, or one of its buckets, actually lands.
 *
 * The drawer has already drilled in and is showing these same choices; this names them
 * again in the content area so the choice is in front of the user rather than only in
 * the left-hand rail. Entering used to jump straight into the first screen, which
 * picked for them and hid the others.
 *
 * Deliberately plain. It is a signpost, not a dashboard: anything richer here would be
 * a screen nobody asked for, sitting between the user and the one they wanted.
 */
export interface SectionIndexProps {
  title: string;
  description?: string;
  sections: readonly ModuleSection[];
  onOpen: (path: string) => void;
}

export function SectionIndex({ title, description, sections, onOpen }: SectionIndexProps) {
  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 600, color: '#212121' }}>
        {title}
      </Typography>
      {description ? (
        <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>
          {description}
        </Typography>
      ) : null}

      <Stack spacing={1} sx={{ mt: 3 }}>
        {sections.map((section) => (
          <Box
            key={section.key}
            component="button"
            type="button"
            onClick={() => onOpen(section.path)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              px: 2,
              py: 1.5,
              textAlign: 'left',
              font: 'inherit',
              cursor: 'pointer',
              borderRadius: '8px',
              border: '1.25px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <Typography sx={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{section.label}</Typography>
            <NextIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
